import React, { useMemo, useState } from 'react'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import VehiclesList from './VehiclesList'
import Vehicle from './Vehicle/Vehicle'
import { Button } from '@demandlocal/components'
import LoadingAnimation from '../../../../components/LoadingAnimation'
import { ReactComponent as AddInventoryImage } from '../../../../images/add_inventory.svg'
import { ReactComponent as InventoryIcon } from '../../../../images/icons/inventory_header_menu.svg'
import { ReactComponent as LogoSimple } from '../../../../images/logo_vertical_colored.svg'
import { ReactComponent as ScrapeIcon } from '../../../../images/icons/linked_icon.svg'
import { ReactComponent as FTPIcon } from '../../../../images/icons/ftp.svg'
import { ReactComponent as ArrowIcon } from '../../../../images/list_arrow.svg'
import InventoryAnalysis from './InventoryAnalysis'
import SoldVehiclesList from './SoldVehiclesList'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import { useGetDMSConnection } from '../../../../api/dealership/profile/useGetDMSConnection'
import MarketedVehiclesList from './MarketedVehiclesList'
import classNames from 'classnames'
import toast from 'react-hot-toast'
import ConnectInventoryScrape from './ConnectNewInventory/ConnectInventoryScrape'
import ConnectInventoryDataProvider from './ConnectNewInventory/ConnectInventoryDataProvider'

const VehiclesTemplate = (): JSX.Element => {
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(true)
  const { data: { totalVehicles = 0 } = {}, isLoading } = useGetOverallStats()
  const { data: connectedDMS = { data: 0 }, isLoading: isLoadingConnectedDMS } =
    useGetDMSConnection()
  const [optionSelected, setOptionSelected] = useState<number | null>(null)
  const navigate = useNavigate()

  const onSubmit = (): void => {
    switch (optionSelected) {
      case 1:
        navigate('./import/website')
        return
      case 2:
        navigate('./import/data')
        return
      case 3:
        window.open(
          'https://form.asana.com/?k=FEMVzYxoz33bWRhfdf8x6A&d=189525429620912',
          '_blank',
        )
        return
      default:
        toast.error('You must select an option before continuing', { duration: 5000 })
        return
    }
  }

  const { pathname } = useLocation()
  const renderImport = useMemo(() => pathname.includes('/import'), [pathname])

  return (
    <div className={'flex flex-1 flex-col h-full'}>
      {isLoading && (
        <div
          className={'flex flex-col h-full my-auto mx-auto items-center justify-center'}>
          <LoadingAnimation />
        </div>
      )}
      {!isLoading && totalVehicles === 0 && (
        <div
          className={
            'flex flex-col  gap-3 my-auto mx-auto items-center justify-center px-10'
          }>
          <AddInventoryImage className="mx-auto max-h-[200px]" />
          <span className={'font-bold text-3xl font-Sparta pb-1 pt-8'}>
            You have no inventory yet
          </span>
          <span className={'text-base font-Spartan pt-1 pb-8'}>
            Every customer you're not targeting is targerted by somebody else, make your
            cars visible!
          </span>
          <div className="flex flex-row *:w-1/3 justify-center gap-3">
            <div
              className={classNames(
                'flex flex-col justify-start items-center p-2 gap-2 rounded-md border-[3px] cursor-pointer',
                {
                  'border-primary-persian-blue': optionSelected === 1,
                  'border-web-gray/20': optionSelected !== 1,
                },
              )}
              onClick={(e) => {
                e.preventDefault()
                setOptionSelected(1)
              }}>
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center justify-center my-5 mx-auto rounded-full w-20 h-20 bg-white border-4 border-primary-persian-blue/10">
                  <ScrapeIcon className="w-14 h-14 px-2" />
                </div>
              </div>
              <span className="font-bold text-center">Import From Your Website</span>
              <span className="text-center">
                Pull directly from your website if you have a specified templated website
                provider
              </span>
              <ul className="p-2 *:pb-2">
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>Automatically pull your inventory based on your website</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>
                    Supports a large amount of website providers, including; Ansira,
                    Dealer Car Search, Dealer Inspire, Dealer.ca, Dealer.com, Dealer Fire,
                    Dealer On, Dealer Trend, Fox Dealer, FuzionZone, Motorfuse, and Team
                    Velocity
                  </span>
                </li>
              </ul>
            </div>
            <div
              className={classNames(
                'flex flex-col justify-start items-center p-2 gap-2 rounded-md border-[3px] cursor-pointer',
                {
                  'border-primary-persian-blue': optionSelected === 2,
                  'border-web-gray/20': optionSelected !== 2,
                },
              )}
              onClick={(e) => {
                e.preventDefault()
                setOptionSelected(2)
              }}>
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center justify-center my-5 mx-auto rounded-full w-20 h-20 bg-white border-4 border-primary-persian-blue/10">
                  <FTPIcon className="object-fill w-10 h-10" />
                </div>
              </div>
              <span className="font-bold text-center">
                Connect Your Inventory Data Provider
              </span>
              <span className="text-center">
                Connect to an already established automated feed of your inventory
              </span>
              <ul className="p-2 *:pb-2">
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>
                    Supports the following platforms: vAuto, HomeNet, and FTP/SFTP.
                  </span>
                </li>
              </ul>
            </div>
            <div
              className={classNames(
                'flex flex-col justify-start items-center p-2 gap-2 rounded-md border-[3px] cursor-pointer',
                {
                  'border-primary-persian-blue': optionSelected === 3,
                  'border-web-gray/20': optionSelected !== 3,
                },
              )}
              onClick={(e) => {
                e.preventDefault()
                setOptionSelected(3)
              }}>
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center justify-center my-5 mx-auto rounded-full w-20 h-20 bg-white border-4 border-primary-persian-blue/10">
                  <LogoSimple className="object-contain px-2" />
                </div>
              </div>
              <span className="font-bold text-center">Let us Help You</span>
              <span className="text-center">
                Work with our LinkOne representative to view inventory
              </span>
              <ul className="p-2 *:pb-2">
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>
                    Have a skilled representative connect your inventory and get you
                    started
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  Analyze and target specific inventory that based on export
                  recommendations
                </li>
              </ul>
            </div>
          </div>
          <Button onClick={onSubmit}>Continue</Button>
          {renderImport && <Outlet />}
        </div>
      )}
      {!isLoading && totalVehicles > 0 && (
        <React.Fragment>
          <PageHeader
            tabMenu={[
              { name: 'Overview', to: 'overview' },
              { name: 'Vehicle Feed', to: 'list' },
              { name: 'Marketed Vehicles', to: 'marketed' },
              {
                name: 'Sold Vehicles',
                to: 'sold-vehicles',
                visible: !isLoadingConnectedDMS && connectedDMS?.data > 0,
              },
            ]}
            icon={<InventoryIcon />}
            title={'Inventory'}
          />
          <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
            <div className={' mb-10'} />

            <Outlet context={[isLoadingReport, setIsLoadingReport]} />
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

const Vehicles = (): JSX.Element => {
  return (
    <Routes>
      <Route element={<VehiclesTemplate />}>
        <Route path="import/data" element={<ConnectInventoryDataProvider />} />
        <Route path="import/website" element={<ConnectInventoryScrape />} />
        <Route path={'overview/*'} element={<InventoryAnalysis />} />
        <Route path={'sold-vehicles'} element={<SoldVehiclesList />} />
        <Route path={'marketed'} element={<MarketedVehiclesList />} />
        <Route path={'list'} element={<VehiclesList />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Route>
      <Route path={'vehicles/:vin'} element={<Vehicle />} />
    </Routes>
  )
}

export default Vehicles
